import React, { useEffect } from "react";
import { MapContainer, TileLayer, Marker, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { useSelector } from "react-redux";
import { isEmpty, isNull } from "lodash";
import Page_Loader from '../../../assets/vizr_images/page_loader.gif';

function LocationMap({ loc = "default" }) {
  // Get data from Dataguard reducer
  const locationData = useSelector(
    (state) => state.DataguardReducer.locationSummary?.locations
  );
  const locationSummaryLoader = useSelector(
    (state) => state.DataguardReducer.locationSummaryLoader
  );

  const center = [20, 0]; // Center on world like SysGuard

  // Transform Dataguard location data to match SysGuard format
  const transformedLocations = locationData?.map((location) => ({
    position: [location.latitude, location.longitude],
    category: location.hasAlert ? "alert" : "safe",
    city: location.city,
    country: location.country,
    title: `${location.city}, ${location.country}`
  })) || [];

  // Create dot icons similar to SysGuard but adapted for Dataguard
  const createDotIcon = (color, width, height) =>
    new L.DivIcon({
      className: "custom-icon",
      html: `<div style="
          background-color: ${color}; 
          border-radius: 50%; 
          width: ${width}px; 
          height: ${height}px;
          border: 2px solid white;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        "></div>`,
      iconSize: [width, height],
      iconAnchor: [width / 2, height / 2],
    });

  // Size based on location type (similar to SysGuard)
  const getSizeBasedOnLoc = () => {
    switch (loc) {
      case "dashboardPopup":
        return 12;
      case "locations":
        return 16;
      default:
        return 10;
    }
  };

  // Get dot icon with dynamic sizing
  const getDotIcon = (color) => {
    const size = getSizeBasedOnLoc();
    return createDotIcon(color, size, size);
  };

  const redDotIcon = () => getDotIcon("#ff2251");
  const greenDotIcon = () => getDotIcon("#2ce68f");

  // Map configurations similar to SysGuard
  const mapConfig = {
    default: {
      mapCenter: center,
      zoom: 1.2,
      className: "dataguard-map",
      zoomControl: false,
      scrollWheelZoom: true,
      dragging: true,
      doubleClickZoom: false,
      touchZoom: true,
      worldCopyJump: true,
      minZoom: 1.2,
      maxZoom: 2,
    },
    dashboardPopup: {
      mapCenter: center,
      zoom: 2,
      className: "dataguard-map",
      zoomControl: true,
      scrollWheelZoom: true,
      dragging: true,
      doubleClickZoom: false,
      touchZoom: false,
      worldCopyJump: false,
      minZoom: 2,
      maxZoom: 8,
      maxBounds: [
        [-85, -170],
        [85, 170],
      ],
    },
    locations: {
      mapCenter: center,
      zoom: 1,
      className: "dataguard-map",
      zoomControl: false,
      scrollWheelZoom: true,
      dragging: true,
      doubleClickZoom: false,
      touchZoom: false,
      worldCopyJump: false,
      minZoom: 1,
      maxZoom: 8,
      maxBounds: [
        [-85, -170],
        [85, 170],
      ],
    },
  };

  const mapSettings = mapConfig[loc] ? mapConfig[loc] : mapConfig.default;

  const renderClassName = () => {
    if (loc === "locations") {
      return "dataguard-map-container location";
    } else if (loc === "dashboardPopup") {
      return "dataguard-map-container dashboard";
    } else {
      return "dataguard-map-container";
    }
  };

  // Filter out invalid coordinates
  const validLocations = transformedLocations.filter((location) => {
    const [lat, lng] = location.position;
    return (
      !isNaN(lat) &&
      !isNaN(lng) &&
      lat >= -90 &&
      lat <= 90 &&
      lng >= -180 &&
      lng <= 180
    );
  });

  if (locationSummaryLoader) {
    return (
      <div className='loader-container'>
        <img src={Page_Loader} alt="Loading..." className='loader-img' />
      </div>
    );
  }

  return (
    <div className={renderClassName()}>
      {mapSettings && !isNull(mapSettings) && !isEmpty(mapSettings) && (
        <MapContainer
          center={mapSettings.mapCenter}
          zoom={mapSettings.zoom}
          className={mapSettings.className}
          zoomControl={mapSettings.zoomControl}
          scrollWheelZoom={mapSettings.scrollWheelZoom}
          dragging={mapSettings.dragging}
          doubleClickZoom={mapSettings.doubleClickZoom}
          touchZoom={mapSettings.touchZoom}
          worldCopyJump={mapSettings.worldCopyJump}
          minZoom={mapSettings.minZoom}
          maxZoom={mapSettings.maxZoom}
          maxBounds={mapSettings.maxBounds}
          whenCreated={(map) => {
            // Invalidate size shortly after mount to fix initial grey map if parent resizes
            setTimeout(() => map.invalidateSize(), 250);
          }}
        >
          <TileLayer
            url="https://cartodb-basemaps-a.global.ssl.fastly.net/rastertiles/voyager/{z}/{x}/{y}.png"
            noWrap={true}
          />

          {/* Helper component to re-invalidate size on first paint and on window resize */}
          <InvalidateSizeOnMount />

          {validLocations.map((location, index) => (
            <Marker
              key={index}
              position={location.position}
              icon={
                location.category === "alert" ? redDotIcon() : greenDotIcon()
              }
              title={location.title}
            />
          ))}
        </MapContainer>
      )}
    </div>
  );
}

export default LocationMap;

// Local component after export to keep main component uncluttered
const InvalidateSizeOnMount = () => {
  const map = useMap();
  useEffect(() => {
    // Delay to ensure CSS/layout applied
    const id = setTimeout(() => {
      map.invalidateSize();
    }, 500);
    const handleResize = () => map.invalidateSize();
    window.addEventListener('resize', handleResize);
    return () => {
      clearTimeout(id);
      window.removeEventListener('resize', handleResize);
    };
  }, [map]);
  return null;
};
